import React, { useState } from 'react';

const PLACEMENT_LABELS = {
  'home': 'Home',
  'home_with_services': 'Home + Services',
  'snf': 'SNF',
  'rehab': 'Rehab (IRF)',
  'ltac': 'LTAC',
  'not_ready': 'Not Ready',
};

function PatientComparison({ wardResults, onBack }) {
  const [patientA, setPatientA] = useState(null);
  const [patientB, setPatientB] = useState(null);

  if (!wardResults || wardResults.length < 2) return null;

  const sorted = [...wardResults].sort((a, b) => a.priority - b.priority);

  const chemBarClass = (val) => {
    if (val >= 0.7) return 'high';
    if (val <= 0.3) return 'low';
    return 'mid';
  };

  const renderSelector = (label, selected, onChange, excludeId) => (
    <div className="pc-selector">
      <label className="pc-selector-label">{label}</label>
      <select
        className="pc-selector-select"
        value={selected?.patientId || ''}
        onChange={(e) => {
          const p = wardResults.find(r => r.patientId === e.target.value);
          onChange(p || null);
        }}
      >
        <option value="">Select Patient</option>
        {sorted.filter(r => r.patientId !== excludeId).map(r => (
          <option key={r.patientId} value={r.patientId}>
            #{r.priority} {r.patientName} — {r.readiness.replace('_', ' ')}
          </option>
        ))}
      </select>
    </div>
  );

  const renderPatientColumn = (patient) => {
    if (!patient) {
      return (
        <div className="pc-column pc-empty">
          <div className="pc-empty-text">Select a patient to compare</div>
        </div>
      );
    }

    const r = patient;
    return (
      <div className="pc-column">
        {/* Header */}
        <div className="pc-patient-header">
          <div className="pc-priority">#{r.priority}</div>
          <div className="pc-name">{r.patientName}</div>
          <div className="pc-meta">Age {r.age} | {r.insurance} | Day {r.daysAdmitted} | {r.unit}</div>
          <div className="pc-diagnosis">{r.diagnosis.code}: {r.diagnosis.label}</div>
          <div className="pc-badges">
            <span className={`pc-placement ${r.placement === 'not_ready' ? 'not-ready' : r.placement === 'home' ? 'home' : ''}`}>
              {PLACEMENT_LABELS[r.placement]}
            </span>
            <span className={`pc-readiness ${r.readiness}`}>
              {r.readiness.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Rationale */}
        <div className="pc-section">
          <div className="pc-section-title">Decision Rationale</div>
          <div className="pc-rationale">{r.rationale}</div>
          {r.nca_factors && (
            <div className="pc-driver">
              Driver: <span>{r.nca_factors.decision_driver}</span>
            </div>
          )}
        </div>

        {/* Barriers */}
        <div className="pc-section">
          <div className="pc-section-title">Barriers</div>
          {r.barriers.length > 0 ? (
            <div className="pc-barriers">
              {r.barriers.map(b => (
                <span key={b} className="pc-barrier-tag">{b.replace(/_/g, ' ')}</span>
              ))}
            </div>
          ) : (
            <div className="pc-no-barriers">No barriers identified</div>
          )}
        </div>

        {/* Key Neurochemical States */}
        <div className="pc-section">
          <div className="pc-section-title">Neurochemical State (Key Signals)</div>
          <div className="pc-chem-grid">
            {Object.entries(r.nca.chemicals).slice(0, 10).map(([name, val]) => (
              <div key={name} className="pc-chem-row">
                <span className="pc-chem-name">{name}</span>
                <div className="pc-chem-bar-bg">
                  <div className={`pc-chem-bar ${chemBarClass(val)}`} style={{ width: `${val * 100}%` }} />
                </div>
                <span className="pc-chem-val">{val.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Brain Region Activations */}
        <div className="pc-section">
          <div className="pc-section-title">H-H Gated Brain Regions</div>
          <div className="pc-gate-grid">
            {Object.entries(r.nca.gates).map(([name, val]) => (
              <div key={name} className="pc-gate-row">
                <span className="pc-gate-name">{name}</span>
                <div className="pc-gate-bar-bg">
                  <div className="pc-gate-bar" style={{ width: `${val * 100}%`, opacity: Math.max(val, 0.15) }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance */}
        <div className="pc-section">
          <div className="pc-section-title">Compliance Flags</div>
          <div className="pc-flags">
            {r.complianceFlags.map((f, i) => (
              <div key={i} className="pc-flag">{f}</div>
            ))}
          </div>
        </div>

        {/* SIRA */}
        <div className="pc-section pc-sira">
          <div className="pc-section-title">SIRA Audit Hash</div>
          <div className="pc-hash">{r.stateHash?.slice(0, 32)}...</div>
          <div className="pc-sira-row">
            <span className="pc-sira-pass">A</span>
            <span className="pc-sira-pass">C</span>
            <span className="pc-sira-pass">O</span>
            <span className="pc-sira-pass">A</span>
          </div>
        </div>
      </div>
    );
  };

  // Build the "why" comparison narrative
  const renderVerdict = () => {
    if (!patientA || !patientB) return null;

    const higher = patientA.priority < patientB.priority ? patientA : patientB;
    const lower = patientA.priority < patientB.priority ? patientB : patientA;

    const higherReady = higher.readiness === 'ready';
    const lowerReady = lower.readiness === 'ready';

    return (
      <div className="pc-verdict">
        <div className="pc-verdict-title">NCA Decision: Why {higher.patientName} Discharges First</div>
        <div className="pc-verdict-body">
          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Priority Ranking</span>
            <span className="pc-verdict-value">
              {higher.patientName} is #{higher.priority}, {lower.patientName} is #{lower.priority}
            </span>
          </div>

          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Readiness</span>
            <span className="pc-verdict-value">
              {higher.patientName}: <span className={`pc-inline-status ${higher.readiness}`}>{higher.readiness.replace('_', ' ')}</span>
              {' vs '}
              {lower.patientName}: <span className={`pc-inline-status ${lower.readiness}`}>{lower.readiness.replace('_', ' ')}</span>
            </span>
          </div>

          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Placement</span>
            <span className="pc-verdict-value">
              {higher.patientName}: {PLACEMENT_LABELS[higher.placement]}
              {' vs '}
              {lower.patientName}: {PLACEMENT_LABELS[lower.placement]}
            </span>
          </div>

          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Barriers</span>
            <span className="pc-verdict-value">
              {higher.patientName}: {higher.barriers.length === 0 ? 'None' : higher.barriers.map(b => b.replace(/_/g, ' ')).join(', ')}
              {' vs '}
              {lower.patientName}: {lower.barriers.length === 0 ? 'None' : lower.barriers.map(b => b.replace(/_/g, ' ')).join(', ')}
            </span>
          </div>

          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Days Admitted</span>
            <span className="pc-verdict-value">
              {higher.patientName}: {higher.daysAdmitted} days vs {lower.patientName}: {lower.daysAdmitted} days
            </span>
          </div>

          {higher.nca_factors && lower.nca_factors && (
            <div className="pc-verdict-row">
              <span className="pc-verdict-label">NCA Decision Driver</span>
              <span className="pc-verdict-value">
                {higher.patientName}: <span style={{ color: 'var(--gold)' }}>{higher.nca_factors.decision_driver}</span>
                {' vs '}
                {lower.patientName}: <span style={{ color: 'var(--gold)' }}>{lower.nca_factors.decision_driver}</span>
              </span>
            </div>
          )}

          <div className="pc-verdict-row">
            <span className="pc-verdict-label">Conflict Level</span>
            <span className="pc-verdict-value">
              {higher.patientName}: {higher.nca.conflictLevel.toFixed(3)}
              {' vs '}
              {lower.patientName}: {lower.nca.conflictLevel.toFixed(3)}
              {higher.nca.conflictLevel < lower.nca.conflictLevel
                ? ` — ${higher.patientName} has lower internal conflict, clearer discharge path`
                : higher.nca.conflictLevel > lower.nca.conflictLevel
                ? ` — ${lower.patientName} has lower internal conflict`
                : ' — Equal conflict levels'}
            </span>
          </div>
        </div>

        <div className="pc-verdict-summary">
          <strong>{higher.patientName}</strong> is prioritized for discharge because:
          {' '}{higher.rationale}
          <br /><br />
          <strong>{lower.patientName}</strong> remains because:
          {' '}{lower.rationale}
        </div>

        <div className="pc-verdict-note">
          Every data point above is auditable through SIRA. Both patients' full neurochemical cascade states,
          decision drivers, and compliance flags are cryptographically hashed and timestamped. This is not a
          black box recommendation. It is a transparent, reproducible, mechanistically explainable clinical decision.
        </div>
      </div>
    );
  };

  return (
    <div className="patient-comparison">
      <button className="dd-back-btn" onClick={onBack}>
        {'\u2190'} Back to Ward Overview
      </button>

      <div className="pc-header">
        <h2 className="pc-title">Patient Discharge Comparison</h2>
        <p className="pc-subtitle">
          Select two patients to see exactly why the NCA prioritizes one for discharge over the other.
          Every factor is transparent, auditable, and traceable through the conductance cascade.
        </p>
      </div>

      <div className="pc-selectors">
        {renderSelector('Patient A', patientA, setPatientA, patientB?.patientId)}
        <div className="pc-vs">vs</div>
        {renderSelector('Patient B', patientB, setPatientB, patientA?.patientId)}
      </div>

      {renderVerdict()}

      <div className="pc-columns">
        {renderPatientColumn(patientA)}
        {renderPatientColumn(patientB)}
      </div>
    </div>
  );
}

export default PatientComparison;
